// src/assets/components/HeaderGroup.jsx
// import React, { forwardRef, isValidElement } from "react";
import React, { isValidElement } from "react";
import "../../styles/colorSystem.css"


/* 공통: 아이콘 렌더러 (문자열/객체/JSX 지원) */
const cx = (...c) => c.filter(Boolean).join(" ");

function Icon({
  icon,
  onClick,
  className = "w-[24px] h-[24px]",
}) {
  if (!icon) return null;

  const content = (() => {
    if (isValidElement(icon)) {
      return icon;
    }
    if (typeof icon === "string") {
      return (
        <img
          src={icon}
          alt=""
          className="w-full h-full object-contain"
          aria-hidden
        />
      );
    }
    if (typeof icon === "object" && icon.src) {
      return (
        <img
          src={icon.src}
          alt={icon.alt ?? ""}
          className={cx("w-full h-full object-contain", icon.className)}
          aria-hidden={icon.alt ? undefined : true}
        />
      );
    }
    return null;
  })();

  const clickable = typeof onClick === "function";

  if (!content) return null;

  // onClick이 있으면 버튼처럼 동작
  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cx(
          "shrink-0 flex items-center justify-center",
          "focus:outline-none",
          className
        )}
      >
        {content}
      </button>
    );
  }

  // 아니면 단순 아이콘
  return (
    <span
      className={cx(
        "shrink-0 flex items-center justify-center",
        className
      )}
    >
      {content}
    </span>
  );
}

/**
 * 공통 헤더 래퍼 : HeaderShell
 * - 제목은 기본적으로 중앙 고정(양쪽 아이콘/버튼 폭에 영향 X)
 * - 타이포 통일: Pretendard, Bold, text-t2(20/28/-1.2%), text-default
 * - 레이아웃 통일: h-64px, px-24, py-18, gap-24, rounded-2xl
 */
const HEADER_TITLE = "text-t2 text-text-default";

export function HeaderShell({
  children,
  className,
  bg = "bg-bg-white",
}) {
  return (
    <div
      className={cx(
        "relative w-full h-[64px] px-[16px] shrink-0",
        "flex items-center justify-between gap-[16px]",
        bg,
        className
      )}
    >
      {children}
    </div>
  );
}

/* 1) 좌 아이콘 · 중앙 타이틀 · 우 아이콘 */
export function HeaderIconLR({
  title = "제목 영역",
  leftIcon,
  rightIcon,
  onLeftClick,
  onRightClick,
  className,
  bg,
}) {
  return (
    <HeaderShell className={className} bg={bg}>
      <Icon icon={leftIcon} onClick={onLeftClick} />
      <div className={cx("flex-1 text-center", HEADER_TITLE)}>{title}</div>
      <Icon icon={rightIcon} onClick={onRightClick} />
    </HeaderShell>
  );
}

/* 2) 중앙 타이틀 · 좌측 아이콘 */
export function HeaderIconL({
  title = "제목 영역",
  icon,
  onIconClick,
  className,
  bg,
}) {
  return (
    <HeaderShell className={className} bg={bg}>
      <Icon icon={icon} onClick={onIconClick} />

      {/* 정중앙 오버레이 타이틀 (아이콘 클릭 막지 않게 pointer-events-none) */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className={HEADER_TITLE}>{title}</div>
      </div>
    </HeaderShell>
  );
}

/* 3) 중앙 타이틀 · 좌측 아이콘  + 오른쪽 액션 텍스트(“입력”) */
export function HeaderIconLActionR({
  title = "제목 영역",
  icon,
  onIconClick,
  actionText = "입력",
  onAction,
  className,
  bg,
}) {
  const actionEnabled = typeof onAction === "function";

  return (
    <HeaderShell className={className} bg={bg}>
      <Icon icon={icon} onClick={onIconClick} />

      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className={HEADER_TITLE}>{title}</div>
      </div>

      <button
        type="button"
        onClick={actionEnabled ? onAction : undefined}
        disabled={!actionEnabled}
        className={`text-b1 ${actionEnabled ? "text-text-subtler" : "text-text-disabled"}`}
      >
        {actionText}
      </button>
    </HeaderShell>
  );
}


/* 4) 좌 아이콘 · 좌정렬 타이틀 · 우측 아이콘 3개 */
export function HeaderIconLIconsR({
  title = "제목 영역",
  leftIcon,
  onLeftClick,
  rightIcons = [], // [{ icon, onClick }] 또는 icon만
  className,
  bg,
}) {
  return (
    <HeaderShell className={className} bg={bg}>
      <div className="flex items-center gap-[16px] flex-1">
        <Icon icon={leftIcon} onClick={onLeftClick} />
        <div className={HEADER_TITLE}>{title}</div>
      </div>
      <div className="flex items-center gap-[16px]">
        {rightIcons.slice(0, 3).map((item, i) => {
          const icon = item?.icon ?? item;
          const onClick = item?.onClick;
          return <Icon key={i} icon={icon} onClick={onClick} />;
        })}
      </div>
    </HeaderShell>
  );
}


/* 5) 큰 타이틀 + 서브텍스트 + (옵션) 우측 아이콘 2개 */
export function HeaderLeftTitleSubtitle({
  title = "매장명을 입력해주세요",
  subtitle = "주소를 입력해주세요",
  rightIcons = [], // [{ icon, onClick }] 또는 icon만
  className,
  bg,
}) {
  return (
    <HeaderShell className={cx("h-[68px]", className)} bg={bg}>
      <div className="flex-1 min-w-0">
        <div className={cx("pt-[18px] pb-[4px]", HEADER_TITLE)}>{title}</div>
        <div className="text-c2 text-text-tertiary truncate">
          {subtitle}
        </div>
      </div>
      <div className="flex items-center gap-[16px]">
        {rightIcons.slice(0, 2).map((item, i) => {
          const icon = item?.icon ?? item;
          const onClick = item?.onClick;
          return <Icon key={i} icon={icon} onClick={onClick} />;
        })}
      </div>
    </HeaderShell>
  );
}